// Source - https://stackoverflow.com/a/45090312
// Posted by Cole
// Retrieved 2026-07-05, License - CC BY-SA 3.0

import { useCallback, useRef } from 'react';

const SVG_STYLE_PROPS = [
  'fill',
  'stroke',
  'stroke-width',
  'stroke-dasharray',
  'opacity',
  'font-family',
  'font-size',
  'font-weight',
  'text-anchor',
  'dominant-baseline',
] as const;

const HTML_STYLE_PROPS = [
  'color',
  'fill',
  'stroke',
  'font-family',
  'font-size',
  'font-weight',
  'display',
  'margin',
  'margin-right',
  'padding',
  'text-align',
  'vertical-align',
  'line-height',
  'list-style',
  'list-style-type',
] as const;

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function getChartSvgElement(container: HTMLElement): SVGSVGElement | null {
  const directChartSvg = container.querySelector('.recharts-wrapper > svg.recharts-surface');
  if (directChartSvg instanceof SVGSVGElement) {
    return directChartSvg;
  }

  const chartSurfaces = Array.from(container.querySelectorAll('svg.recharts-surface')).filter(
    (svg): svg is SVGSVGElement => svg instanceof SVGSVGElement && !svg.closest('.recharts-legend-wrapper'),
  );

  if (!chartSurfaces.length) {
    const fallbackSvg = container.querySelector('svg');
    return fallbackSvg instanceof SVGSVGElement ? fallbackSvg : null;
  }

  return chartSurfaces.reduce((largest, current) => {
    const largestRect = largest.getBoundingClientRect();
    const currentRect = current.getBoundingClientRect();
    return currentRect.width * currentRect.height > largestRect.width * largestRect.height ? current : largest;
  });
}

function parseViewBox(viewBox: string | null) {
  if (!viewBox) {
    return null;
  }

  const parts = viewBox.trim().split(/[\s,]+/).map(Number);
  if (parts.length !== 4 || parts.some((value) => Number.isNaN(value))) {
    return null;
  }

  return { x: parts[0], y: parts[1], width: parts[2], height: parts[3] };
}

function getExportDimensions(svg: SVGSVGElement) {
  const viewBox = parseViewBox(svg.getAttribute('viewBox'));
  if (viewBox && viewBox.width > 0 && viewBox.height > 0) {
    return {
      width: viewBox.width,
      height: viewBox.height,
      viewBox: `${viewBox.x} ${viewBox.y} ${viewBox.width} ${viewBox.height}`,
    };
  }

  const attributeWidth = Number(svg.getAttribute('width'));
  const attributeHeight = Number(svg.getAttribute('height'));
  if (attributeWidth > 0 && attributeHeight > 0) {
    return {
      width: attributeWidth,
      height: attributeHeight,
      viewBox: `0 0 ${attributeWidth} ${attributeHeight}`,
    };
  }

  const { width, height } = svg.getBoundingClientRect();
  const exportWidth = Math.max(Math.round(width), 1);
  const exportHeight = Math.max(Math.round(height), 1);

  return {
    width: exportWidth,
    height: exportHeight,
    viewBox: `0 0 ${exportWidth} ${exportHeight}`,
  };
}

function inlineComputedStyles(source: SVGSVGElement, target: SVGSVGElement) {
  const sourceNodes = [source, ...Array.from(source.querySelectorAll('*'))];
  const targetNodes = [target, ...Array.from(target.querySelectorAll('*'))];

  sourceNodes.forEach((sourceNode, index) => {
    const targetNode = targetNodes[index];
    if (!(sourceNode instanceof SVGElement) || !(targetNode instanceof SVGElement)) {
      return;
    }

    const computed = window.getComputedStyle(sourceNode);
    SVG_STYLE_PROPS.forEach((prop) => {
      const value = computed.getPropertyValue(prop);
      if (value) {
        targetNode.style.setProperty(prop, value);
      }
    });
  });
}

function inlineHtmlComputedStyles(source: Element, target: Element) {
  const sourceNodes = [source, ...Array.from(source.querySelectorAll('*'))];
  const targetNodes = [target, ...Array.from(target.querySelectorAll('*'))];

  sourceNodes.forEach((sourceNode, index) => {
    const targetNode = targetNodes[index];
    if (!(sourceNode instanceof Element) || !(targetNode instanceof Element)) {
      return;
    }

    const computed = window.getComputedStyle(sourceNode);
    const styleProps = targetNode instanceof SVGElement ? SVG_STYLE_PROPS : HTML_STYLE_PROPS;

    styleProps.forEach((prop) => {
      const value = computed.getPropertyValue(prop);
      if (value) {
        (targetNode as HTMLElement | SVGElement).style.setProperty(prop, value);
      }
    });
  });
}

function prepareChartSvgClone(svg: SVGSVGElement) {
  const clone = svg.cloneNode(true) as SVGSVGElement;
  const { width, height, viewBox } = getExportDimensions(svg);

  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  clone.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
  clone.setAttribute('viewBox', viewBox);
  clone.setAttribute('width', String(width));
  clone.setAttribute('height', String(height));
  clone.style.overflow = 'visible';

  inlineComputedStyles(svg, clone);

  return { clone, width, height, viewBox };
}

function buildLegendForeignObject(
  legendWrapper: HTMLElement,
  wrapperRect: DOMRect,
) {
  const legendRect = legendWrapper.getBoundingClientRect();
  if (legendRect.width <= 0 || legendRect.height <= 0) {
    return null;
  }

  const legendClone = legendWrapper.cloneNode(true) as HTMLElement;
  inlineHtmlComputedStyles(legendWrapper, legendClone);

  legendClone.style.position = 'static';
  legendClone.style.left = 'auto';
  legendClone.style.right = 'auto';
  legendClone.style.top = 'auto';
  legendClone.style.bottom = 'auto';
  legendClone.style.width = `${legendRect.width}px`;
  legendClone.style.height = `${legendRect.height}px`;
  legendClone.style.margin = '0';

  const foreignObject = document.createElementNS('http://www.w3.org/2000/svg', 'foreignObject');
  foreignObject.setAttribute('x', String(legendRect.left - wrapperRect.left));
  foreignObject.setAttribute('y', String(legendRect.top - wrapperRect.top));
  foreignObject.setAttribute('width', String(legendRect.width));
  foreignObject.setAttribute('height', String(legendRect.height));

  const xhtmlRoot = document.createElement('div');
  xhtmlRoot.setAttribute('xmlns', 'http://www.w3.org/1999/xhtml');
  xhtmlRoot.appendChild(legendClone);
  foreignObject.appendChild(xhtmlRoot);

  return foreignObject;
}

function serializeChartForExport(chartSvg: SVGSVGElement, container: HTMLElement): string {
  const { clone, width, height, viewBox } = prepareChartSvgClone(chartSvg);
  const wrapper = container.querySelector('.recharts-wrapper');
  const legendWrapper = container.querySelector('.recharts-legend-wrapper');

  if (!(wrapper instanceof HTMLElement) || !(legendWrapper instanceof HTMLElement)) {
    return new XMLSerializer().serializeToString(clone);
  }

  const wrapperRect = wrapper.getBoundingClientRect();
  const exportWidth = Math.max(Math.round(wrapperRect.width), width);
  const exportHeight = Math.max(Math.round(wrapperRect.height), height);
  const legendForeignObject = buildLegendForeignObject(legendWrapper, wrapperRect);

  if (!legendForeignObject) {
    return new XMLSerializer().serializeToString(clone);
  }

  const composite = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  composite.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  composite.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
  composite.setAttribute('width', String(exportWidth));
  composite.setAttribute('height', String(exportHeight));
  composite.setAttribute('viewBox', `0 0 ${exportWidth} ${exportHeight}`);

  const chartLayer = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  chartLayer.setAttribute('x', '0');
  chartLayer.setAttribute('y', '0');
  chartLayer.setAttribute('width', String(exportWidth));
  chartLayer.setAttribute('height', String(exportHeight));
  chartLayer.setAttribute('viewBox', viewBox);
  chartLayer.setAttribute('preserveAspectRatio', 'xMidYMid meet');

  while (clone.firstChild) {
    chartLayer.appendChild(clone.firstChild);
  }

  composite.appendChild(chartLayer);
  composite.appendChild(legendForeignObject);

  return new XMLSerializer().serializeToString(composite);
}

export default function useExportChartHandler(filename = 'chart') {
  const chartContainerRef = useRef<HTMLDivElement>(null);

  const exportChart = useCallback(
    (asSVG = true) => {
      const container = chartContainerRef.current;
      if (!container) {
        return false;
      }

      const chartSvg = getChartSvgElement(container);
      if (!chartSvg) {
        return false;
      }

      if (asSVG) {
        const svgString = serializeChartForExport(chartSvg, container);
        const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
        downloadBlob(blob, `${filename}.svg`);
      } else {
        const wrapper = container.querySelector('.recharts-wrapper');
        const html = `<!DOCTYPE html><html><body>${wrapper?.outerHTML ?? chartSvg.outerHTML}</body></html>`;
        const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
        downloadBlob(blob, `${filename}.html`);
      }

      return true;
    },
    [filename],
  );

  return { chartContainerRef, exportChart };
}
